import { useRef, useState } from 'react'
import { uploadDocument, type UploadTarget } from '../lib/upload'
import { useI18n } from '../i18n/useI18n'

/**
 * Prise de photo puis envoi, en un seul appui.
 *
 * capture="environment" ouvre directement l'appareil photo arriere sur
 * Android, sans passer par la galerie : c'est le geste attendu quand on
 * est devant une facture ou un bordereau. La galerie et les PDF restent
 * accessibles par le second bouton.
 *
 * L'image est reduite avant l'envoi, voir lib/upload.ts.
 */
export function PhotoField({
  label,
  target,
  documentId,
  onUploaded,
  required,
}: {
  label: string
  target: UploadTarget
  documentId: string | null
  onUploaded: (documentId: string | null) => void
  required?: boolean
}) {
  const { t } = useI18n()
  const cameraRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<'idle' | 'sending'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function handle(file: File | undefined) {
    if (!file) return
    setError(null)
    setState('sending')
    try {
      const res = await uploadDocument(file, target)
      onUploaded(res.documentId)
      const ko = Math.max(1, Math.round(res.byteSize / 1024))
      setInfo(t('photo_sent', { ko }))
    } catch (e) {
      // On ne perd pas ce que l'utilisateur a deja tape : seul l'envoi
      // de la piece a echoue, le formulaire reste tel quel.
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setState('idle')
    }
  }

  return (
    <div className="photofield">
      <span className="field__label">
        {label}
        {required && <span className="field__required"> *</span>}
      </span>

      {documentId ? (
        <div className="photofield__done">
          <span className="chip chip--ok">{t('photo_attached')}</span>
          {info && <span className="field__hint">{info}</span>}
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => {
              onUploaded(null)
              setInfo(null)
            }}
          >
            {t('photo_replace')}
          </button>
        </div>
      ) : (
        <div className="photofield__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={state === 'sending'}
            onClick={() => cameraRef.current?.click()}
          >
            {state === 'sending' ? t('photo_sending') : t('photo_take')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={state === 'sending'}
            onClick={() => fileRef.current?.click()}
          >
            {t('photo_choose')}
          </button>
        </div>
      )}

      {error && (
        <p className="field__error">
          {t('photo_failed')} {error}
        </p>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => void handle(e.target.files?.[0])}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => void handle(e.target.files?.[0])}
      />
    </div>
  )
}
